import React, { useEffect, useRef, useState } from "react";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {
	faCircleCheck,
	faCircleInfo,
	faCircleXmark,
	faTriangleExclamation
} from "@awesome.me/kit-92187fb46c/icons/classic/solid";

const {FontAwesomeIcon} = await import("@fortawesome/react-fontawesome");

export type ToastLevel = "info" | "success" | "warn" | "error";

interface IToaster {
  uuid: string;
  message: string;
  timeout?: number;
  level: ToastLevel;
  onRemove: (uuid: string) => void;
}

const Toaster: React.FC<IToaster> = ({ uuid, message, timeout, level, onRemove }) => {
  const toasterRef = useRef<HTMLDivElement>(null);
  const toasterTimeoutRef = useRef<HTMLDivElement>(null);
  const [iconProps, setIconProps] = useState<{ icon: IconDefinition; color: string }>({
    icon: faCircleInfo,
    color: "var(--primary)",
  });

  useEffect(() => {
    if (level == "info") {
      setIconProps({ icon: faCircleInfo, color: "var(--primary)" });
    } else if (level == "success") {
      setIconProps({ icon: faCircleCheck, color: "var(--success)" });
    } else if (level == "warn") {
      setIconProps({ icon: faTriangleExclamation, color: "var(--warn)" });
    } else if (level == "error") {
      setIconProps({ icon: faCircleXmark, color: "var(--danger)" });
    }
  }, [level]);

  useEffect(() => {
    restartTimer();
  }, [message, timeout, level]);

  const closeToaster = () => {
    if (toasterRef.current) {
      toasterRef.current.classList.add("closing");
      setTimeout(() => {
        onRemove(uuid);
      }, 175);
    }
  };

  const restartTimer = () => {
    if (toasterTimeoutRef.current) {
      toasterTimeoutRef.current.getAnimations().forEach((animation) => {
        if ((animation as CSSAnimation).animationName === "onProgress") {
          animation.cancel();
          animation.play();
        }
      });
    }
  };

  const pauseTimer = () => {
    if (toasterTimeoutRef.current) {
      toasterTimeoutRef.current.getAnimations().forEach((animation) => {
        if ((animation as CSSAnimation).animationName === "onProgress") {
          animation.pause();
        }
      });
    }
  };

  const startTimer = () => {
    if (toasterTimeoutRef.current) {
      toasterTimeoutRef.current.getAnimations().forEach((animation) => {
        if ((animation as CSSAnimation).animationName === "onProgress") {
          animation.play();
        }
      });
    }
  };

  return (
    <div
      ref={toasterRef}
      id={`toaster_${uuid}`}
      className={`toaster ${level}`}
      onAnimationEnd={(evt: React.AnimationEvent<HTMLDivElement>) => {
        if (timeout && evt.animationName === "onProgress") {
          closeToaster();
        }
      }}
      onMouseEnter={pauseTimer}
      onMouseLeave={() => {
        startTimer();
        toasterRef.current?.classList.remove("clicked");
      }}
      onMouseDown={() => {
        toasterRef.current?.classList.add("clicked");
      }}
      onMouseUp={() => {
        toasterRef.current?.classList.remove("clicked");
        closeToaster();
      }}
    >
      <div>
        <FontAwesomeIcon icon={iconProps.icon} color={`${iconProps.color}`} />
      </div>
      <div>{message}</div>
      {timeout && (
        <div ref={toasterTimeoutRef} className="toaster-timeout" style={{ animationDuration: `${timeout}ms` }}></div>
      )}
    </div>
  );
};

export default Toaster;
